import { useEffect, useState } from "react";
import { RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUSES = ["New", "Contacted", "In Review", "Closed"];
const COUNT_CLS = { New: "text-emerald-600", Contacted: "text-sky-600", "In Review": "text-[#6D28D9]", Closed: "text-slate-500" };
const SELECT_CLS = {
  New: "border-emerald-600/40 text-emerald-700",
  Contacted: "border-sky-600/40 text-sky-700",
  "In Review": "border-[#6D28D9]/50 text-[#6D28D9]",
  Closed: "border-slate-300 text-slate-500",
};

export const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/leads");
      setLeads(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/leads/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${leads.length} enquiries to CSV.`);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-leads-modal">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Investor Enquiries</h2>
          <p className="mt-1 text-sm text-slate-500">{leads.length} total · pipeline updates save instantly</p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-refresh-leads-button"
            onClick={load}
            className="flex items-center gap-2 border border-slate-200 px-4 py-2.5 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-700 transition-colors hover:border-[#6D28D9]/40 hover:text-[#6D28D9]"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            data-testid="admin-export-csv-button"
            onClick={exportCsv}
            disabled={exporting || leads.length === 0}
            className="flex items-center gap-2 bg-[#6D28D9] px-4 py-2.5 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-colors hover:bg-[#5B21B6] disabled:opacity-40"
          >
            <Download className="h-3.5 w-3.5" /> {exporting ? "Preparing…" : "Export CSV"}
          </button>
        </div>
      </div>

      <div data-testid="pipeline-summary" className="mt-8 grid grid-cols-2 gap-px border border-slate-200 bg-slate-200 sm:grid-cols-4">
        {STATUSES.map((s) => (
          <div key={s} data-testid={`pipeline-count-${s.toLowerCase().replace(" ", "-")}`} className="bg-[#F6F4FD] px-5 py-4">
            <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">{s}</p>
            <p className={`font-display mt-1 text-2xl font-semibold ${COUNT_CLS[s]}`}>{leads.filter((l) => l.status === s).length}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto border border-slate-200">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-[#F6F4FD] font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-5 py-4">Investor</th>
              <th className="px-5 py-4">Contact</th>
              <th className="px-5 py-4">Share of Interest</th>
              <th className="px-5 py-4">Range</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Received</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-b border-slate-100 transition-colors hover:bg-violet-50">
                <td className="px-5 py-4 font-medium text-slate-800">{l.name}</td>
                <td className="px-5 py-4 text-slate-500">
                  <div>{l.email}</div>
                  <div className="text-xs text-slate-500">{l.phone}</div>
                </td>
                <td className="px-5 py-4 text-[#6D28D9]">{l.share_interest}</td>
                <td className="px-5 py-4 text-slate-500">{l.investment_range}</td>
                <td className="px-5 py-4">
                  <select
                    data-testid={`admin-lead-status-select-${l.id}`}
                    value={l.status}
                    onChange={async (e) => {
                      try {
                        await api.patch(`/leads/${l.id}/status`, { status: e.target.value });
                        load();
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    className={`cursor-pointer border bg-[#FFFFFF] px-2.5 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.18em] outline-none transition-colors hover:border-[#6D28D9]/50 ${SELECT_CLS[l.status] || "border-slate-300 text-slate-500"}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-5 py-4 text-xs text-slate-500">{new Date(l.created_at).toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {leads.length === 0 && !loading && (
          <p data-testid="admin-leads-empty" className="px-5 py-12 text-center font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">No enquiries yet</p>
        )}
      </div>
    </div>
  );
};
